import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSession } from './useSession'
import {
  getAgencyTeamMembers,
  getWorkSummary,
  getTeamAssignmentStats,
} from '../api/agencyTeam'
import type { TeamMember, WorkSummary } from '../types/agencyTeam'
import { queryKeys } from '../utils/queryKeys'

export function useTeam() {
  const { user } = useSession()

  const userId = user?.id
  const agencyId = (user as any)?.agency_id
  const userRole = (user as any)?.role
  const isAdmin = userRole === 'admin' || userRole === 'superadmin'

  // All consumers of useTeam() share this single cached request — React Query
  // deduplicates concurrent calls with the same key and caches for 5 min (global default).
  const membersQuery = useQuery({
    queryKey: queryKeys.agencyTeam.members(agencyId ?? ''),
    queryFn: async () => {
      let members = await getAgencyTeamMembers(agencyId!)

      // If the agency has no members yet, fall back to the current user so
      // self-assignment dropdowns still work.
      if (members.length === 0 && user) {
        members = [{
          id: user.id,
          email: user.email || '',
          first_name: (user as any).first_name ?? null,
          last_name: (user as any).last_name ?? null,
          full_name: (user as any).first_name && (user as any).last_name
            ? `${(user as any).first_name} ${(user as any).last_name}`
            : (user as any).first_name || (user as any).last_name || user.email || null,
          avatar_url: (user as any).avatar_url ?? (user as any).avatar ?? null,
          role: userRole ?? 'agent',
          agency_id: agencyId,
          lead_agent_id: null,
          is_lead_agent: false,
          permission_overrides: null,
          created: (user as any).created ?? '',
          updated: (user as any).updated ?? '',
        }]
      }

      return members
    },
    enabled: !!agencyId && !!userId,
  })

  const workSummaryQuery = useQuery({
    queryKey: queryKeys.agencyTeam.workSummary(userId ?? '', agencyId ?? ''),
    queryFn: () => getWorkSummary(userId!, agencyId!),
    enabled: !!agencyId && !!userId,
  })

  const assignmentStatsQuery = useQuery({
    queryKey: queryKeys.agencyTeam.assignmentStats(agencyId ?? ''),
    queryFn: () => getTeamAssignmentStats(agencyId!),
    enabled: !!agencyId && isAdmin,
  })

  const teamMembers: TeamMember[] = membersQuery.data ?? []

  const getMember = useCallback(
    (memberId: string): TeamMember | undefined => teamMembers.find(m => m.id === memberId),
    [teamMembers],
  )

  const canManageMember = useCallback((_memberId: string): boolean => isAdmin, [isAdmin])

  const loadTeamData = useCallback(() => {
    membersQuery.refetch()
    workSummaryQuery.refetch()
    assignmentStatsQuery.refetch()
  }, [membersQuery, workSummaryQuery, assignmentStatsQuery])

  return {
    loading: membersQuery.isLoading,
    error: membersQuery.error ? (membersQuery.error as any).message ?? 'Failed to load team data' : null,
    teamMembers,
    workSummary: workSummaryQuery.data ?? null,
    assignmentStats: assignmentStatsQuery.data ?? null,
    userId,
    agencyId,
    isAdmin,
    loadTeamData,
    getMember,
    canManageMember,
  }
}

export function useTeamContext() {
  const { user } = useSession()

  const userId = user?.id
  const agencyId = (user as any)?.agency_id
  const userRole = (user as any)?.role
  const isAdmin = userRole === 'admin' || userRole === 'superadmin'

  return { userId, agencyId, isAdmin, userRole }
}
